import type {ConnectorId} from '@cohort/shared/schema/apps';
import {getTriggerIntegrationSpec} from '@cohort/shared/schema/apps';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import type {App} from '@cohort/wallet/apps';
import OAuthConnectButton from '@cohort/wallet/apps/OAuthConnectButton';
import {getAppFromTriggerIntegration} from '@cohort/wallet/apps/TriggerIntegration';
import {useVerifyStep} from '@cohort/wallet/apps/useStepVerification';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import ActionCtaComponentWrapper from '@cohort/wallet/components/space/ActionCtaComponentWrapper';
import {Spinner} from '@cohort/wallet/components/Spinner';
import {useMerchantContext} from '@cohort/wallet/hooks/useMerchantContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import type {JourneyStepTriggerWDto} from '@cohort/wallet-schemas/journeySteps';
import {useTranslation} from 'react-i18next';

const ButtonLikeWrapper: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {hasDarkBg} = useThemeContext();

  return (
    <div
      className={classNames(
        'flex cursor-not-allowed items-center justify-center rounded-lg px-4 py-2 text-sm font-medium',
        hasDarkBg ? 'bg-white/30 text-black/80' : 'bg-black/4 text-black/40'
      )}
    >
      {children}
    </div>
  );
};

type TriggerCtaProps = {
  journeyId: string;
  journeyParticipationId: string;
  journeyStepId: string;
  trigger: JourneyStepTriggerWDto;
  xpsRedirectUrl: string;
};

type VerifyButtonProps = Pick<TriggerCtaProps, 'journeyId' | 'journeyStepId'> & {
  journeyParticipationId: string;
  triggerId: string;
  merchantId: string;
  app: App;
  connectionId: string | null;
  isLoading: boolean;
  processStepVerification: () => void;
};

const VerifyButton: React.FC<VerifyButtonProps> = ({
  app,
  journeyId,
  journeyParticipationId,
  journeyStepId,
  triggerId,
  isLoading,
  processStepVerification,
}) => {
  const {t} = useTranslation('apps', {keyPrefix: 'triggerCta'});
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const hasUserConnection = app.spec.userConnector !== null;

  return (
    <PrimaryButton
      size="small"
      className="w-full md:w-auto"
      type="button"
      text={hasUserConnection ? t('verifyWith', {appName: app.spec.name}) : t('verify')}
      tracking={{
        namespace: 'journeys.detail.verify',
        metadata: {
          journeyId,
          appId: app.spec.id,
          stepId: journeyStepId,
          triggerId,
          journeyParticipationId,
        },
      }}
      isLoading={isLoading}
      onClick={() => processStepVerification()}
    />
  );
};

const TriggerCta: React.FC<TriggerCtaProps> = ({
  journeyId,
  journeyParticipationId,
  journeyStepId,
  trigger,
  xpsRedirectUrl,
}) => {
  const merchant = useMerchantContext();
  const {t} = useTranslation('apps', {keyPrefix: 'triggerCta'});
  const currentApp = getAppFromTriggerIntegration(trigger.triggerIntegrationId);
  const triggerSpec = getTriggerIntegrationSpec(trigger.triggerIntegrationId);

  const {processStepVerification, isConnectionFetching, isVerificationLoading, userConnection} =
    useVerifyStep(
      trigger,
      currentApp,
      journeyId,
      journeyParticipationId,
      journeyStepId,
      merchant.id
    );

  if (isConnectionFetching) {
    return (
      <ButtonLikeWrapper>
        <Spinner height="h-5" width="w-5" />
      </ButtonLikeWrapper>
    );
  }

  if (trigger.verificationAttemptsLeft === 0) {
    return <ButtonLikeWrapper>{t('noMoreVerificationAttempts')}</ButtonLikeWrapper>;
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (currentApp.spec.userConnector !== null && !userConnection) {
    return (
      <OAuthConnectButton
        app={currentApp}
        size="small"
        className="w-full md:w-auto"
        type="button"
        text={t('connectApp', {appName: currentApp.spec.name})}
        tracking={{
          namespace: 'journeys.detail.connect',
          metadata: {
            journeyId,
            appId: currentApp.spec.id,
            stepId: journeyStepId,
            triggerId: trigger.id,
            journeyParticipationId,
          },
        }}
        connectorId={currentApp.spec.userConnector.id as ConnectorId}
        xpsRedirectUrl={xpsRedirectUrl}
      />
    );
  }

  if (triggerSpec.type === 'verification') {
    return (
      <VerifyButton
        journeyId={journeyId}
        journeyParticipationId={journeyParticipationId}
        journeyStepId={journeyStepId}
        triggerId={trigger.id}
        merchantId={merchant.id}
        app={currentApp}
        connectionId={userConnection?.id || null}
        isLoading={isVerificationLoading}
        processStepVerification={processStepVerification}
      />
    );
  }

  // triggerSpec === 'action
  return (
    <ActionCtaComponentWrapper
      trigger={trigger}
      processStepVerification={processStepVerification}
      journeyId={journeyId}
      journeyStepId={journeyStepId}
      trackingData={{
        journeyId,
        appId: currentApp.spec.id,
        stepId: journeyStepId,
        triggerId: trigger.id,
        journeyParticipationId,
      }}
    />
  );
};

export default TriggerCta;
